/*
 * Copyright (c) Microsoft Corporation. All rights reserved. Licensed under the MIT license.
 * See LICENSE in the project root for license information.
 */

import { getAIEmailResponse } from "../helpers/middle-tier-calls";
import { MESSAGES, clearNotifications, showError, showNotification } from "../helpers/message-helper";
import { getMessagesByConversationId } from "../helpers/sso-helper";
import { generateRequestBody, getConversationId, getProviderEmailAddress, validateAIResponse } from "./helper";
import { ConsoleLogger } from "../helpers/error-handler";

/* global global */

Office.onReady(() => {
  // If needed, Office.js is ready to be called
});

/**
 * Shows a notification when the add-in command is executed.
 * @param event
 */

export async function action(event) {
  try {
    showNotification(MESSAGES.loading);

    const item = Office.context.mailbox.item;
    const conversationId = getConversationId(item);
    // Resolve the mailbox owner up front. For shared / delegated mailboxes this is the
    // owner email (e.g. reservations@hotel.com), not the signed-in delegate. We need it
    // *before* the Graph fetch so we can target /users/{owner}/messages instead of
    // /me/messages (the latter only sees the signed-in user's own inbox).
    const providerEmailAddress = await getProviderEmailAddress(item);
    const signedInEmailAddress = Office.context.mailbox.userProfile.emailAddress || "";
    const isSharedMailbox =
      providerEmailAddress && providerEmailAddress.toLowerCase() !== signedInEmailAddress.toLowerCase();
    const graphOwnerEmail = isSharedMailbox ? providerEmailAddress : null;

    // Set by the on-prem manifest (Commands.Url ...commands.html?onprem=1). On-prem mailboxes can't
    // use SSO/Graph, so skip the login entirely and draft from the open email. Cloud installs keep
    // the original Graph flow, including showing errors when it fails.
    const isOnPremInstall = /[?&]onprem=1(?:&|$)/.test(window.location.search);

    const draftReply = async (response) => {
      try {
        if (response?.endActionEvent) {
          event.completed();
          return;
        }

        if (response?.error) {
          throw new Error(response.error);
        }

        const payload = await generateRequestBody(response.value, providerEmailAddress);
        // Tells on-prem drafts (no Graph by design) apart from cloud ones in the logs.
        if (isOnPremInstall) payload.thread_source = "onprem";

        // API call to fetch AI response........
        const AIResponse = await getAIEmailResponse(payload);

        ConsoleLogger(AIResponse, "action");

        // Validate AI response..............
        const { isValid, errorMessage } = validateAIResponse(AIResponse);

        if (!isValid) {
          showNotification(errorMessage, () => {
            event.completed();
          });
          return;
        }

        item.displayReplyForm(AIResponse.email_body);

        clearNotifications(() => {
          event.completed();
        });

        // Be sure to indicate when the add-in command function is complete
      } catch (error) {
        ConsoleLogger(error, "action catch");

        showError(error, () => {
          event.completed();
        });
      }
    };

    if (isOnPremInstall) {
      await draftReply({ value: [] });
    } else {
      await getMessagesByConversationId(conversationId, graphOwnerEmail, draftReply);
    }
  } catch (error) {
    showError(error, () => {
      event.completed();
    });
  }
}

function getGlobal() {
  return typeof self !== "undefined"
    ? self
    : typeof window !== "undefined"
    ? window
    : typeof global !== "undefined"
    ? global
    : undefined;
}

const g = getGlobal();

// the add-in command functions need to be available in global scope
g.action = action;
